import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@clerk/react";
import { toast } from "sonner";
import { ArrowLeft, MessageSquare, Send, HandCoins, Check, X, Repeat } from "lucide-react";
import { useAuthedApi, errMsg, formatINR, imgUrl } from "../lib/api";
import { useRealtime } from "../lib/realtime";

const time = (iso) => new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
const STATUS = { pending: "text-amber-400", accepted: "text-emerald-400", declined: "text-red-400", countered: "text-gray-400", withdrawn: "text-gray-500" };

function ThreadList({ threads, active }) {
  if (!threads) return <p className="p-4 text-sm text-gray-500">Loading...</p>;
  if (!threads.length) return <div className="p-8 text-center text-sm text-gray-400" data-testid="chat-empty">No chats yet. Open a car and tap <strong>Make an Offer</strong> or <strong>Chat with seller</strong>.</div>;
  return threads.map((t) => (
    <Link key={t.id} to={`/chat/${t.id}`} className={`flex gap-3 p-3 border-b border-white/5 hover:bg-white/[0.03] ${active === t.id ? "bg-[#D4AF37]/10" : ""}`} data-testid={`chat-thread-${t.id}`}>
      <img src={imgUrl(t.listing?.images?.[0])} alt="" className="w-14 h-11 rounded-lg object-cover shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="flex justify-between gap-2"><p className="text-sm font-semibold truncate">{t.listing ? `${t.listing.year} ${t.listing.brand} ${t.listing.model}` : "Listing removed"}</p>
          {t.my_unread > 0 && <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-[10px] font-bold flex items-center justify-center" data-testid={`chat-unread-${t.id}`}>{t.my_unread}</span>}</div>
        <p className="text-[11px] text-gray-400 truncate">{t.role === "buyer" ? `Seller: ${t.seller_name}` : `Buyer: ${t.buyer_name}`}</p>
        <p className="text-[11px] text-gray-500 truncate">{t.last_message || "No messages yet"}</p>
      </div>
    </Link>
  ));
}

function OfferCard({ m, mine, bounds, onRespond }) {
  const [counter, setCounter] = useState(null);
  const n = Number(counter);
  const err = counter && (n < bounds[0] || n > bounds[1]) ? `Offers must be between ${formatINR(bounds[0])} and ${formatINR(bounds[1])}` : "";
  const st = m.offer.status;
  return (
    <div className={`rounded-2xl border p-4 w-[280px] ${mine ? "border-[#D4AF37]/60 bg-[#D4AF37]/10" : "border-white/10 bg-[#161922]"}`} data-testid={`offer-card-${m.id}`}>
      <p className="text-[10px] uppercase tracking-widest text-gray-400 flex items-center gap-1"><HandCoins className="w-3 h-3 text-[#D4AF37]" />{m.offer.counter_of ? "Counter offer" : "Offer"} · {mine ? "You" : "Them"}</p>
      <p className="font-display text-3xl gold-text mt-1" data-testid={`offer-card-amount-${m.id}`}>{formatINR(m.offer.amount)}</p>
      {m.text && <p className="text-xs text-gray-300 mt-1">{m.text}</p>}
      <p className={`text-xs font-semibold mt-2 capitalize ${STATUS[st]}`} data-testid={`offer-card-status-${m.id}`}>{st}</p>
      {st === "pending" && !mine && (
        counter === null ? (
          <div className="grid grid-cols-3 gap-1.5 mt-3">
            <button onClick={() => onRespond(m.id, "accept")} className="rounded-full bg-emerald-600 hover:bg-emerald-500 text-xs font-semibold py-1.5 flex items-center justify-center gap-1" data-testid={`offer-accept-${m.id}`}><Check className="w-3 h-3" />Accept</button>
            <button onClick={() => onRespond(m.id, "decline")} className="rounded-full border border-red-500/50 text-red-300 text-xs py-1.5 flex items-center justify-center gap-1" data-testid={`offer-decline-${m.id}`}><X className="w-3 h-3" />Decline</button>
            <button onClick={() => setCounter("")} className="rounded-full border border-[#D4AF37]/50 text-[#E5C158] text-xs py-1.5 flex items-center justify-center gap-1" data-testid={`offer-counter-${m.id}`}><Repeat className="w-3 h-3" />Counter</button>
          </div>
        ) : (
          <div className="mt-3 space-y-1.5">
            <input className="lux-input" type="number" autoFocus value={counter} onChange={(e) => setCounter(e.target.value)} placeholder="Counter amount (₹)" data-testid={`offer-counter-input-${m.id}`} />
            <p className={`text-[10px] ${err ? "text-red-400" : "text-gray-500"}`}>{err || `Allowed: ${formatINR(bounds[0])} – ${formatINR(bounds[1])}`}</p>
            <div className="flex gap-1.5">
              <button onClick={() => setCounter(null)} className="btn-outline !py-1 text-xs flex-1">Cancel</button>
              <button disabled={!counter || !!err} onClick={() => onRespond(m.id, "counter", n)} className="btn-gold !py-1 text-xs flex-1 disabled:opacity-40" data-testid={`offer-counter-send-${m.id}`}>Send</button>
            </div>
          </div>
        )
      )}
    </div>
  );
}

function ThreadView({ id, onChange }) {
  const api = useAuthedApi();
  const { userId } = useAuth();
  const { subscribe, live, refresh } = useRealtime();
  const [t, setT] = useState(null);
  const [text, setText] = useState("");
  const end = useRef(null);
  const load = () => api("get", `/threads/${id}`).then((r) => { setT(r); refresh(); }).catch((e) => setT({ error: errMsg(e) }));

  useEffect(() => { setT(null); load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => subscribe((ev) => { if (ev.type === "message" && ev.thread_id === id) { load(); onChange(); } }), [id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (live) return; const p = setInterval(load, 5000); return () => clearInterval(p); }, [live, id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (end.current) end.current.scrollTop = end.current.scrollHeight; }, [t?.messages?.length]);

  const send = async (e) => {
    e.preventDefault();
    const body = text.trim();
    if (!body) return;
    setText("");
    try { await api("post", `/threads/${id}/messages`, { text: body }); load(); onChange(); } catch (err) { toast.error(errMsg(err)); setText(body); }
  };
  const respond = async (mid, action, amount) => {
    try { await api("post", `/threads/${id}/offers/${mid}/respond`, { action, amount }); toast.success(action === "counter" ? "Counter offer sent" : `Offer ${action}ed`); load(); onChange(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  if (!t) return <p className="p-6 text-sm text-gray-500">Loading chat...</p>;
  if (t.error) return <p className="p-6 text-sm text-red-300">{t.error}</p>;
  const car = t.listing;
  return (
    <div className="flex flex-col h-full" data-testid="chat-thread-view">
      <Link to="/chat" className="lg:hidden flex items-center gap-1 text-xs text-gray-400 px-3 pt-3" data-testid="chat-back"><ArrowLeft className="w-3.5 h-3.5" />All chats</Link>
      {car ? (
        <Link to={`/cars/${car.id}`} className="flex items-center gap-3 p-3 border-b gold-line bg-[#0d0f14]" data-testid="chat-car-banner">
          <img src={imgUrl(car.images?.[0])} alt="" className="w-16 h-12 rounded-lg object-cover" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold truncate">{car.year} {car.brand} {car.model} {car.variant}</p>
            <p className="text-xs"><span className="gold-text font-semibold">{formatINR(car.price)}</span> · <span className={car.status === "sold" ? "text-red-400" : "text-emerald-400"} data-testid="chat-car-status">{car.status === "sold" ? "Sold" : "Available"}</span> · <span className="text-gray-400">{car.city}</span></p>
          </div>
          <span className="text-[10px] text-gray-500 hidden sm:block">{t.role === "buyer" ? t.seller_name : t.buyer_name}</span>
        </Link>
      ) : <p className="p-3 border-b gold-line text-xs text-gray-400">This listing was removed.</p>}
      <div ref={end} className="flex-1 overflow-y-auto p-3 space-y-3" data-testid="chat-messages">
        {t.messages.map((m) => {
          const mine = m.sender_id === userId;
          if (m.kind === "system") return <p key={m.id} className="text-center text-[11px] text-gray-400" data-testid={`chat-system-${m.id}`}>{m.text}</p>;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div>
                {m.kind === "offer" ? <OfferCard m={m} mine={mine} bounds={car?.offer_bounds || [0, 0]} onRespond={respond} /> : (
                  <p className={`max-w-[280px] sm:max-w-md px-3.5 py-2 rounded-2xl text-sm whitespace-pre-wrap break-words ${mine ? "text-black rounded-br-sm" : "bg-[#1b1e27] rounded-bl-sm"}`} style={mine ? { background: "var(--gold-grad)" } : {}} data-testid={`chat-msg-${m.id}`}>{m.text}</p>
                )}
                <p className={`text-[9px] text-gray-500 mt-0.5 ${mine ? "text-right" : ""}`}>{time(m.created_at)}</p>
              </div>
            </div>
          );
        })}
      </div>
      <form onSubmit={send} className="p-3 border-t border-white/5 flex gap-2">
        <input className="lux-input flex-1" value={text} maxLength={2000} onChange={(e) => setText(e.target.value)} placeholder="Type a message..." data-testid="chat-input" />
        <button className="btn-gold !px-4" disabled={!text.trim()} data-testid="chat-send-btn"><Send className="w-4 h-4" /></button>
      </form>
    </div>
  );
}

export default function Chat() {
  const { id } = useParams();
  const api = useAuthedApi();
  const navigate = useNavigate();
  const { subscribe } = useRealtime();
  const [threads, setThreads] = useState(null);
  const load = () => api("get", "/threads").then(setThreads).catch(() => setThreads([]));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => subscribe((ev) => ev.type === "message" && load()), []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!id && threads?.length && window.innerWidth >= 1024) navigate(`/chat/${threads[0].id}`, { replace: true }); }, [threads, id, navigate]);

  return (
    <div className="max-w-7xl mx-auto px-0 sm:px-6 lg:px-8 pt-[100px] lg:pt-28 pb-20 lg:pb-6" data-testid="chat-page">
      <h1 className="font-display text-3xl px-4 sm:px-0 mb-3 flex items-center gap-2"><MessageSquare className="w-6 h-6 text-[#D4AF37]" />Chats & Offers</h1>
      <div className="lux-card overflow-hidden grid lg:grid-cols-[320px_1fr] h-[calc(100vh-220px)] lg:h-[calc(100vh-170px)] min-h-[420px]">
        <aside className={`${id ? "hidden lg:block" : "block"} border-r border-white/5 overflow-y-auto`} data-testid="chat-thread-list"><ThreadList threads={threads} active={id} /></aside>
        <section className={`${id ? "block" : "hidden lg:block"} min-h-0`}>{id ? <ThreadView id={id} onChange={load} /> : <p className="p-8 text-center text-sm text-gray-500">Select a chat</p>}</section>
      </div>
    </div>
  );
}
